import { FileUp, Loader2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { cn } from "../lib/cn";
import { parsePdfFile, type Sentence } from "../lib/pdf";
import type { Id } from "../../convex/_generated/dataModel";

export function DropZone({
  onDocReady,
  folderId,
}: {
  onDocReady: (doc: {
    title: string;
    folderId: Id<"folders"> | null;
    fullText: string;
    sentences: Sentence[];
    file: File;
  }) => void;
  folderId: Id<"folders"> | null;
}) {
  const [hover, setHover] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      if (
        !file.name.toLowerCase().endsWith(".pdf") &&
        file.type !== "application/pdf"
      ) {
        setError("Please drop a PDF file.");
        return;
      }
      setReading(true);
      try {
        const { fullText, sentences } = await parsePdfFile(file);
        if (!sentences.length) {
          throw new Error("No readable text found in that PDF.");
        }
        onDocReady({
          title: file.name.replace(/\.pdf$/i, ""),
          folderId,
          fullText,
          sentences,
          file,
        });
      } catch (caught) {
        setError(
          caught instanceof Error ? caught.message : "Failed to parse PDF.",
        );
      } finally {
        setReading(false);
      }
    },
    [folderId, onDocReady],
  );

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(event) => {
        event.preventDefault();
        setHover(false);
        const file = event.dataTransfer.files?.[0];
        if (file) void handleFile(file);
      }}
      onClick={() => inputRef.current?.click()}
      className={cn(
        "glass-panel relative flex w-full max-w-2xl cursor-pointer flex-col items-center justify-center rounded-3xl border border-dashed px-8 py-20 text-center transition-all",
        hover
          ? "scale-[1.01] border-primary/60"
          : "border-glass-border hover:border-primary/40",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      <div className="mb-5 grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-primary/30 to-accent/30 ring-1 ring-white/10">
        {reading ? (
          <Loader2 className="h-7 w-7 animate-spin" />
        ) : (
          <FileUp className="h-7 w-7" />
        )}
      </div>
      <h2 className="font-display text-3xl tracking-tight">
        {reading ? "Reading your document…" : "Drop a PDF to begin"}
      </h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Echo Read strips citations, page furniture, and reference clutter — then
        reads every word aloud, in order.
      </p>
      {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}
    </div>
  );
}
